import { useEffect, useState } from "react";
import { GetStaticProps } from "next";
import Link from "next/link";

import styles from "../../styles/traits/Traits.module.css";

import archetypesJsonData from "../../assets/traitInfo/archetypes.json";
import elementsJsonData from "../../assets/traitInfo/elements.json";
import patternsJsonData from "../../assets/traitInfo/patterns.json";

import ProfileHeader from "@/components/publicProfiles/Header/ProfileHeader";
import BreadCrumbs from "@/components/shared/breadCrumbs/BreadCrumbs";
import TraitsLeftBar from "@/components/traits/shared/TraitsLeftBar";
import Element from "@/components/traits/element/Element/Element";
import Archetype from "@/components/traits/arcehtype/Archetypes/Archetype";
import Pattern from "@/components/traits/pattern/Pattern/Pattern";
import {
  filterElements,
  filterByDimension,
  filterByNature,
  filterByArchetype,
  sortElements,
} from "../../components/traits/shared/traitFilters";
import { usePublicProfileContext } from "@/context/PublicProfileContext";
import MobileSearchPage from "@/components/publicProfiles/Header/MobileSearchPage";
import useScrollBlock from "../../helpers/useScroll";
import FilterIcon from "@/components/shared/FilterIcon";
import TraitMobileFilter from "@/components/traits/shared/TraitsMobileFilter/TraitMobileFilter";
import { traitsMapper } from "@/components/traits/shared/traitsMap";
import GhostBox from "@/components/shared/GhostBox/GhostBox";
import MagnifyingGlassImage from "@/components/shared/GhostBox/MagnifyingGlassImage";
import {
  ExtendedScale,
  ExtendedPattern,
  ExtendedArchetype,
  TraitElement,
} from "@/models/sharedModels";
import Footers from "@/components/shared/Footers/Footers";
import TraitsSEOHeader from "@/components/traits/shared/TraitsSEOHeader";
import LargeTraitsHeading from "@/components/traits/shared/LargeTraitsHeading/LargeTraitsHeading";
import CTAModalAccount from "@/components/shared/CTAModal/CTAModalAccount";
import XMark from "@/components/shared/XMark";

type TraitsHomePageProps = {
  elements: ExtendedScale[];
  patterns: ExtendedPattern[];
  archetypes: ExtendedArchetype[];
};

type TraitElementMap = TraitElement[];

export default function TraitsHome(props: TraitsHomePageProps) {
  const [crumbs, setCrumbs] = useState<string[]>(["Traits"]);
  const [traitType, setTraitType] = useState<string[] | []>([]);
  const [dimension, setDimension] = useState<string[] | []>([]);
  const [nature, setNature] = useState<string[] | []>([]);
  const [archetypeType, setArchetypeType] = useState<string[] | []>([]);
  const [currentSort, setCurrentSort] = useState<string>("alphabetically");
  const { mobileSearchOpen, modal, setModal } = usePublicProfileContext();
  const [blockScroll, allowScroll] = useScrollBlock();
  const [mobileFilterPageOpen, setMobileFilterPageOpen] =
    useState<boolean>(false);

  const allElements = useGetAllElemntsPatternsArchtypes(props);

  useEffect(() => {
    setTraitType(["element"]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const main = document.getElementById("mainTraits");
    const modalParent = document.getElementById("modalParent");
    if (modal === true && main) {
      if (modalParent) {
        modalParent.style.height = (main.clientHeight / 2).toString() + "px";
      }
    }

    if (modal === true) {
      blockScroll();
    } else {
      allowScroll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal]);

  function removeFilter(str: string) {
    // @ts-ignore
    if (traitType.includes(str)) {
      setTraitType(traitType.filter((item: any) => item !== str));
    }
    // @ts-ignore
    if (dimension.includes(str)) {
      setDimension(dimension.filter((item: any) => item !== str));
    }
    // @ts-ignore
    if (nature.includes(str)) {
      setNature(nature.filter((item: any) => item !== str));
    }
    // @ts-ignore
    if (archetypeType.includes(str)) {
      setArchetypeType(archetypeType.filter((item: any) => item !== str));
    }
  }

  const mapped = allElements
    ?.filter((el) => filterElements(el, traitType))
    .filter((el) => filterByDimension(el, dimension))
    .filter((el) => filterByNature(el, nature))
    .filter((el) => filterByArchetype(el, archetypeType))
    .sort((a, b) => sortElements(a, b, currentSort))
    .map((el) => {
      if (el.traitContent.traitType === "element") {
        return (
          <Link
            href={`/traits/elements/${el.slug}`}
            key={el.slug}
            className="elBodyNoMarginMobile"
          >
            <Element slug={el.slug} />
          </Link>
        );
      } else if (el.traitContent.traitType === "archetype") {
        const arch = el as ExtendedArchetype;
        if (arch.dimensionSlug !== "spirit") {
          return (
            <Link
              className="elBodyNoMarginMobile"
              href={`/traits/archetypes/${arch.slug}`}
              key={arch.slug}
            >
              <Archetype
                // @ts-ignore
                archetype={arch}
              />
            </Link>
          );
        }
      } else {
        return (
          <Link
            className="elBodyNoMarginMobile"
            href={`/traits/patterns/${el.slug}`}
            key={el.slug}
          >
            <Pattern patternSlug={el.slug} />
          </Link>
        );
      }
    });

  const mappedFilters = [traitType, dimension, nature, archetypeType]
    .flat()
    .map((fil, index) => {
      const mapper = traitsMapper;
      return (
        <div
          onClick={() => {
            removeFilter(fil);
          }}
          className={styles.filterListDiv}
          key={index}
        >
          <p>{mapper[fil]}</p>
          <XMark color="#928E8C" />
        </div>
      );
    });

  return (
    <>
      <TraitsSEOHeader
        title={`Dimensional || All Traits`}
        description="Elements, Patterns, Archetypes, Cognition, Primary Traits, Values, Interaction Styles, Interests, Attachment Styles, Love Styles, Love Attitudes, Conflict Styles, Strengths, Passion & Sensitivity, Openness & Curiosity, Warmth & Agreeableness, Order & Responsibility, Tranquility, Energy & Excitement, Ambition & Self-Enhancement."
      />
      <TraitMobileFilter
        defualtSelected="element"
        display={mobileFilterPageOpen}
        setTraitType={(trait) => {
          setTraitType((traits: any) => [...traits, trait]);
        }}
        removeTraitType={(trait) => {
          setTraitType(traitType.filter((item: any) => item !== trait));
        }}
        setDimension={(dim) => {
          setDimension((current) => [...current, dim]);
        }}
        removeDimension={(dim) => {
          setDimension(dimension.filter((item: any) => item !== dim));
        }}
        setNature={(nat) => {
          setNature((current) => [...current, nat]);
        }}
        removeNature={(nat) => {
          setNature(nature.filter((item: any) => item !== nat));
        }}
        setArchetype={(nat) => {
          setArchetypeType((current) => [...current, nat]);
        }}
        removeArchetype={(nat) => {
          setArchetypeType(archetypeType.filter((item: any) => item !== nat));
        }}
        closeModal={(e) => {
          setMobileFilterPageOpen(false);
        }}
        traitType={traitType}
        dimension={dimension}
        nature={nature}
        archetypeType={archetypeType}
      />
      {mobileSearchOpen === true && <MobileSearchPage />}
      <ProfileHeader />
      <div
        id="mainTraits"
        onClick={(e) => {
          modal ? setModal(false) : null;
        }}
        className={styles.main}
      >
        <div className={styles.top}>
          <BreadCrumbs list={crumbs} urls={[]} />
          <div className={styles.headingAndFilters}>
            <LargeTraitsHeading title="Trait Encyclopedia" />
            <div
              onClick={() => {
                setMobileFilterPageOpen(true);
              }}
              className={styles.MobileFilterDiv}
            >
              <p>Filters</p>
              <div className={styles.filterIcon}>
                <FilterIcon />
              </div>
            </div>
          </div>
        </div>
        <div className={styles.filterListParent}>{mappedFilters}</div>
        <div className={styles.parent}>
          <TraitsLeftBar
            defualtSelected="element"
            setTraitType={(trait) => {
              setTraitType((traits: any) => [...traits, trait]);
            }}
            removeTraitType={(trait) => {
              setTraitType(traitType.filter((item: any) => item !== trait));
            }}
            setDimension={(dim) => {
              setDimension((current) => [...current, dim]);
            }}
            removeDimension={(dim) => {
              setDimension(dimension.filter((item: any) => item !== dim));
            }}
            setNature={(nat) => {
              setNature((current) => [...current, nat]);
            }}
            removeNature={(nat) => {
              setNature(nature.filter((item: any) => item !== nat));
            }}
            setArchetype={(nat) => {
              setArchetypeType((current) => [...current, nat]);
            }}
            removeArchetype={(nat) => {
              setArchetypeType(
                archetypeType.filter((item: any) => item !== nat)
              );
            }}
          />
          <div className={styles.rightBar}>
            <div className={styles.sortBarParent}>
              <p className={styles.sortTitle}>Sort by:</p>
              <p
                className={
                  currentSort === "alphabetically"
                    ? styles.sortSelected
                    : styles.sortNonSelected
                }
                onClick={() => setCurrentSort("alphabetically")}
              >
                A-Z
              </p>
              <p
                className={
                  currentSort === "userScore"
                    ? styles.sortSelected
                    : styles.sortNonSelected
                }
                onClick={() => setCurrentSort("userScore")}
              >
                AVG. USER SCORE
              </p>
              <p
                onClick={() => {
                  setModal(true);
                }}
                className={styles.sortNonSelected}
              >
                YOUR SCORE
              </p>
            </div>
            <div className={styles.mappedParent}>
              <div className={styles.elementsDiv}>{mapped}</div>
            </div>
            {mapped && mapped.length === 0 && (
              <div className={styles.ghostBoxParent}>
                <GhostBox
                  onlyMessage
                  message="No results found"
                  image={<MagnifyingGlassImage />}
                />
              </div>
            )}
          </div>
        </div>
        {modal === true && (
          <CTAModalAccount
            HeadingText="Account required"
            userName={null}
            uid={"null"}
          />
        )}
        <Footers />
      </div>
    </>
  );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  let elements: any[] = [];
  let patterns: any[] = [];
  let archetypes: any[] = [];

  elementsJsonData.forEach((elem) => {
    if (elem.isDiscoverable === true) {
      elements.push(elem);
    }
  });
  patternsJsonData.forEach((patt) => {
    if (patt.isDiscoverable === true) {
      patterns.push(patt);
    }
  });
  archetypesJsonData.forEach((arch) => {
    if (arch.isDiscoverable === true) {
      archetypes.push(arch);
    }
  });

  return {
    props: {
      elements: elements,
      patterns: patterns,
      archetypes: archetypes,
    },
  };
};

export function useGetAllElemntsPatternsArchtypes(props: TraitsHomePageProps) {
  const [allElements, setAllElements] = useState<TraitElementMap | null>(null);

  useEffect(() => {
    let array: TraitElementMap = [];
    props.archetypes.forEach((arch) => {
      if (arch.archetypeGroupSlug !== "values") {
        array.push(arch);
      }
    });
    props.elements.forEach((elem) => {
      array.push(elem);
    });

    props.patterns.forEach((patt) => {
      array.push(patt);
    });
    setAllElements(array);
  }, []);

  return allElements;
}
